import { useCommandHandler } from "@fleet/shortcuts";
import { COMMAND_IDS } from "@/lib/shortcuts/defaults";
import { addToMyDayMutation } from "@listto/shared";
import type { PowerSyncDrizzleDB, Todo } from "@listto/powersync";
import type { VirtualRow } from "../types";

interface ListShortcutsDeps {
  rowData: () => VirtualRow[];
  focusedIndex: () => number | null;
  setFocusedIndex: (idx: number | null) => void;
  setIsKeyboardFocus: (value: boolean) => void;
  drizzle: () => PowerSyncDrizzleDB | null | undefined;
  toggleSelectionMode: () => void;
  setSelectedIds: (next: Set<string>) => void;
  currentListTodos: () => Todo[] | undefined;
}

function findFocusableEdge(rows: VirtualRow[], direction: "first" | "last"): number | null {
  const start = direction === "first" ? 0 : rows.length - 1;
  const end = direction === "first" ? rows.length : -1;
  const step = direction === "first" ? 1 : -1;
  for (let i = start; i !== end; i += step) {
    const row = rows[i];
    if (row && (row.kind === "pending" || row.kind === "completed")) {
      return i;
    }
  }
  return null;
}

/**
 * Registers list-scope command handlers. Extracted from TodoListView to keep
 * the parent component under the max-statements lint cap; the shortcut engine
 * resolves bindings against this scope, so all handlers here run only while
 * a list view is mounted.
 */
export function useListShortcuts(deps: ListShortcutsDeps): void {
  const focusEdge = (direction: "first" | "last") => {
    const idx = findFocusableEdge(deps.rowData(), direction);
    if (idx === null) {
      return;
    }
    deps.setIsKeyboardFocus(true);
    deps.setFocusedIndex(idx);
  };

  useCommandHandler(COMMAND_IDS.TODO_FOCUS_FIRST, () => {
    focusEdge("first");
  });
  useCommandHandler(COMMAND_IDS.TODO_FOCUS_LAST, () => {
    focusEdge("last");
  });

  useCommandHandler(COMMAND_IDS.TODO_ADD_TO_MY_DAY, () => {
    const idx = deps.focusedIndex();
    if (idx === null) {
      return;
    }
    const row = deps.rowData()[idx];
    if (!row || (row.kind !== "pending" && row.kind !== "completed")) {
      return;
    }
    const dbInstance = deps.drizzle();
    if (!dbInstance) {
      return;
    }
    const include = !row.todo.my_day_date;
    // Mutations verify the write and THROW if it does not land (see
    // `packages/shared/src/powersync/verified-write.ts`): without this catch the
    // rejection would surface as an unhandled rejection.
    void addToMyDayMutation(dbInstance, row.todo.id, include).catch((err: unknown) => {
      console.error("Error toggling my day:", err);
    });
  });

  useCommandHandler(COMMAND_IDS.SELECTION_TOGGLE, () => {
    deps.toggleSelectionMode();
  });

  useCommandHandler(COMMAND_IDS.SELECTION_SELECT_ALL, () => {
    const data = deps.currentListTodos();
    if (!data) {
      return;
    }
    deps.setSelectedIds(new Set(data.map((todo) => todo.id)));
  });
}
